/**
 * Confirm-checklist client mirror (S2.3) — same evaluation order and machine
 * blocker refs as the handler's `evaluateConfirmChecklist` (S2.1), computed
 * from detail-page reads so the 確認檢核 card renders live state without a
 * dedicated endpoint. The SERVER result (returned by a failed confirm)
 * always outranks this mirror when present — see followUps for exposing a
 * GET checklist endpoint so the mirror can retire.
 *
 * Blocker refs (machine-readable; `localizeChecklistBlockers` renders them):
 * - customer_active: `customer:{id}`
 * - products_ready: `{productId}:not_found|inactive|not_ready` (server) or
 *   `{productId}:not_ready` (mirror — the Ready feed can't split the two)
 * - price_resolvable: `{productId}`
 * - required_fields: `expectedShipDate` | `deliveryDate` | `lines` | `qty:{lineId}`
 */

import type {
  ConfirmChecklistItem,
  ConfirmChecklistResult,
  SalesOrder,
} from '@/types/api/salesOrder';

/**
 * Mirror of the S2.1 confirm gate. `readyProductIds` = Ready + active
 * products (the same feed that gates the SO picker); `activeCustomerIds` =
 * active customers. Evaluates ALL items — never short-circuits — so the
 * card renders every row.
 */
export function computeConfirmChecklist(
  so: SalesOrder,
  readyProductIds: ReadonlySet<string>,
  activeCustomerIds: ReadonlySet<number>
): ConfirmChecklistResult {
  const items: ConfirmChecklistItem[] = [];

  const customerActive = activeCustomerIds.has(so.customerId);
  items.push({
    key: 'customer_active',
    ok: customerActive,
    blockers: customerActive ? [] : [`customer:${so.customerId}`],
  });

  const productBlockers = so.lines
    .filter((line) => !readyProductIds.has(line.productId))
    .map((line) => `${line.productId}:not_ready`);
  items.push({
    key: 'products_ready',
    ok: productBlockers.length === 0,
    blockers: productBlockers,
  });

  const priceBlockers = so.lines
    .filter(
      (line) => !(line.unitPrice > 0 && (line.resolvedUnitPrice !== null || line.overrideReason))
    )
    .map((line) => line.productId);
  items.push({
    key: 'price_resolvable',
    ok: priceBlockers.length === 0,
    blockers: priceBlockers,
  });

  // Credit stub — always passes until a credit module lands (plan §S2.1).
  items.push({ key: 'credit', ok: true, blockers: [] });

  const fieldBlockers: string[] = [];
  if (!so.expectedShipDate) fieldBlockers.push('expectedShipDate');
  if (!so.deliveryDate) fieldBlockers.push('deliveryDate');
  if (so.lines.length === 0) fieldBlockers.push('lines');
  for (const line of so.lines) {
    if (!(line.qty > 0)) fieldBlockers.push(`qty:${line.id}`);
  }
  items.push({
    key: 'required_fields',
    ok: fieldBlockers.length === 0,
    blockers: fieldBlockers,
  });

  return { ok: items.every((item) => item.ok), items };
}

/** i18n-key + interpolation pair for one localized blocker row. */
export interface LocalizedBlocker {
  key: string;
  values?: Record<string, string>;
}

const PRODUCT_SUFFIX_KEYS: Record<string, string> = {
  not_found: 'orders:so.detail.checklist.blockers.productNotFound',
  inactive: 'orders:so.detail.checklist.blockers.productInactive',
  not_ready: 'orders:so.detail.checklist.blockers.productNotReady',
};

const FIELD_KEYS: Record<string, string> = {
  expectedShipDate: 'orders:so.detail.checklist.blockers.missingExpectedShipDate',
  deliveryDate: 'orders:so.detail.checklist.blockers.missingDeliveryDate',
  lines: 'orders:so.detail.checklist.blockers.noLines',
};

/**
 * Machine blocker refs → localized rows. Product/line ids resolve to SKUs
 * through the SO's own lines (display mirrors snapshotted at pricing time).
 */
export function localizeChecklistBlockers(
  item: ConfirmChecklistItem,
  so: SalesOrder
): LocalizedBlocker[] {
  const skuByProductId = new Map(so.lines.map((line) => [line.productId, line.sku]));
  const skuByLineId = new Map(so.lines.map((line) => [line.id, line.sku]));

  return item.blockers.map((ref) => {
    switch (item.key) {
      case 'customer_active':
        return { key: 'orders:so.detail.checklist.blockers.customerInactive' };
      case 'products_ready': {
        const splitAt = ref.lastIndexOf(':');
        const productId = splitAt > 0 ? ref.slice(0, splitAt) : ref;
        const suffix = splitAt > 0 ? ref.slice(splitAt + 1) : 'not_ready';
        return {
          key: PRODUCT_SUFFIX_KEYS[suffix] ?? PRODUCT_SUFFIX_KEYS.not_ready,
          values: { sku: skuByProductId.get(productId) ?? productId },
        };
      }
      case 'price_resolvable':
        return {
          key: 'orders:so.detail.checklist.blockers.priceUnresolved',
          values: { sku: skuByProductId.get(ref) ?? ref },
        };
      case 'required_fields': {
        if (ref.startsWith('qty:')) {
          const lineId = ref.slice(4);
          return {
            key: 'orders:so.detail.checklist.blockers.invalidQty',
            values: { sku: skuByLineId.get(lineId) ?? lineId },
          };
        }
        return { key: FIELD_KEYS[ref] ?? FIELD_KEYS.lines };
      }
      default:
        return { key: 'orders:so.detail.checklist.blockers.generic' };
    }
  });
}
